import { Box, Grid, Typography } from "@mui/material";
import { NWONLottieWrapper, useBooleanState } from "@nvon/react-toolbox";
import Image from "next/image";
import { isValidElement, useEffect } from "react";
import useInView from "react-cool-inview";
import {
  headerHeight,
  upToSmallDevices,
} from "../../../helpers/clientSide/constants";
import { useTrigger } from "../../../hooks/useTrigger";
import HtmlFromLangFile from "../../01-atoms/HtmlFromLangFile/HtmlFromLangFile";
import {
  MediaPosition,
  NWONTextWithImagesProps,
} from "./NWONTextWithImagesInterfaces";
import { useNWONTextWithImagesStyles } from "./NWONTextWithImagesStyles";

const NWONTextWithImages = (props: NWONTextWithImagesProps): JSX.Element => {
  const { classes, cx } = useNWONTextWithImagesStyles();

  const { trigger: triggerPlaying, activateTrigger: playAnimation } =
    useTrigger();

  const { trigger: triggerReset, activateTrigger: resetAnimation } =
    useTrigger();

  const {
    state: isInView,
    on: setIsInViewTrue,
    off: setIsInViewFalse,
  } = useBooleanState(false);

  useEffect(() => {
    if (isInView) {
      playAnimation();
    } else {
      resetAnimation();
    }
  }, [isInView, playAnimation, resetAnimation]);

  const { observe } = useInView({
    threshold: isInView ? 0 : 1,
    rootMargin: `-${headerHeight}px 0px 0px 0px`,
    onEnter: setIsInViewTrue,
    onLeave: setIsInViewFalse,
  });

  const image =
    props.illustration &&
    (isValidElement(props.illustration) ? (
      props.illustration
    ) : (
      <Box className={classes.imageWrapper}>
        <Image
          src={props.illustration}
          alt={props.title}
          placeholder="blur"
          layout="responsive"
          sizes={`${upToSmallDevices} 100vw, 50vw`}
          className={classes.image}
        />
      </Box>
    ));

  const animation = props.animation && (
    <Box ref={observe} className={classes.animationWrapper}>
      <NWONLottieWrapper
        lottieFileJson={props.animation}
        triggerReset={triggerReset}
        triggerPlaying={triggerPlaying}
      />
    </Box>
  );

  const media = (
    <Grid
      item={true}
      xs={12}
      md={6}
      className={cx(image && classes.mediaWrapper, classes.orderSecond)}
    >
      {image || animation}
    </Grid>
  );

  return (
    <Grid item={true} container={true} className={classes.wrapper}>
      {props.mediaPosition === MediaPosition.Left && media}

      <Grid
        item={true}
        xs={12}
        md={6}
        className={cx(classes.info, classes.orderFirst)}
      >
        <Typography variant="h5" component="h3">
          <HtmlFromLangFile contentContainingHtml={props.title} />
        </Typography>

        <Typography variant="body1" component="div">
          <HtmlFromLangFile contentContainingHtml={props.description} />
        </Typography>
      </Grid>

      {props.mediaPosition === MediaPosition.Right && media}
    </Grid>
  );
};

export default NWONTextWithImages;
